import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faColumns, faSave, faSpinner, faChevronDown, faChevronRight, faCheckDouble, faTimes, faShopify, faMagic, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faShopify as fabShopify } from '@fortawesome/free-brands-svg-icons';
import { ColumnTogglePopover } from './ColumnTogglePopover';
import '../css/advancedProducts.css';

export const HeaderBar = ({
    gridRef,
    productCount = 0,
    selectedCount = 0,
    dirtyCount = 0,
    onSave,
    isSaving = false,
    totalProducts = 0,
    groupsExpanded = true,
    onToggleGroups,
    selectAllMode = false,
    onSelectAll,
    onClearSelection,
    onPushToShopify,
    isPushing = false,
    // AI Enrichment props
    onEnrich,
    isEnriching = false,
    onApproveEnrichment,
    isApproving = false,
    enrichedCount = 0,
    children
}) => {
    const navigate = useNavigate();
    const [showColumnPopover, setShowColumnPopover] = useState(false);
    const columnBtnRef = useRef(null);

    const handleBack = () => {
        navigate(-1);
    };

    const handleSave = () => {
        if (onSave && dirtyCount > 0) {
            onSave();
        }
    };

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnBtnRef.current && !columnBtnRef.current.contains(event.target)) {
                setShowColumnPopover(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="products-header-bar">
            <div className="header-left">
                <button className="back-button" onClick={handleBack}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Back</span>
                </button>
                {children}

                {/* Select All / Clear Selection */}
                {onSelectAll && (
                    <button
                        className="toggle-btn"
                        onClick={selectAllMode ? onClearSelection : onSelectAll}
                        title={selectAllMode ? 'Clear selection' : 'Select all visible products'}
                    >
                        <FontAwesomeIcon icon={selectAllMode ? faTimes : faCheckDouble} />
                        <span>{selectAllMode ? 'Clear' : 'Select All'}</span>
                    </button>
                )}
            </div>
            
            <div className="header-center">
                {selectAllMode ? (
                    <span style={{ color: '#17a2b8' }}>
                        <strong>Select All Mode</strong> - Actions will apply to ALL {totalProducts.toLocaleString()} products
                    </span>
                ) : selectedCount > 0 ? (
                    <span><strong>{selectedCount}</strong> of {productCount} products selected</span>
                ) : (
                    <span>Editing <strong>{productCount}</strong> products</span>
                )}
                {dirtyCount > 0 && (
                    <span style={{ marginLeft: '12px', color: '#ffc107' }}>
                        • {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'}
                    </span>
                )}
            </div>
            
            <div className="header-right">
                {/* AI Enrich button */}
                {onEnrich && (
                    <button
                        className="enrich-btn"
                        onClick={onEnrich}
                        disabled={selectedCount === 0 || isEnriching}
                        title={selectedCount === 0 ? 'Select products to enrich' : `Enrich ${selectedCount} products with AI`}
                        style={{
                            backgroundColor: isEnriching ? '#6c757d' : '#6f42c1',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: selectedCount === 0 || isEnriching ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: selectedCount === 0 ? 0.6 : 1,
                            marginRight: '8px'
                        }}
                    >
                        {isEnriching ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Enriching...</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faMagic} />
                                <span>AI Enrich</span>
                            </>
                        )}
                    </button>
                )}

                {/* Approve Enrichment button */}
                {onApproveEnrichment && enrichedCount > 0 && (
                    <button
                        className="approve-btn"
                        onClick={onApproveEnrichment}
                        disabled={enrichedCount === 0 || isApproving}
                        title={`Approve enrichment for ${enrichedCount} products`}
                        style={{
                            backgroundColor: isApproving ? '#6c757d' : '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: enrichedCount === 0 || isApproving ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginRight: '8px'
                        }}
                    >
                        {isApproving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Approving...</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faCheck} />
                                <span>Approve ({enrichedCount})</span>
                            </>
                        )}
                    </button>
                )}

                {/* Push to Shopify button */}
                {onPushToShopify && (
                    <button
                        className="shopify-push-btn"
                        onClick={onPushToShopify}
                        disabled={selectedCount === 0 || isPushing}
                        title={selectedCount === 0 ? 'Select products to push' : `Push ${selectedCount} products to Shopify`}
                        style={{
                            backgroundColor: isPushing ? '#6c757d' : '#96bf48',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: selectedCount === 0 || isPushing ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: selectedCount === 0 ? 0.6 : 1
                        }}
                    >
                        {isPushing ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Pushing...</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={fabShopify} />
                                <span>Push to Shopify</span>
                            </>
                        )}
                    </button>
                )}

                <div ref={columnBtnRef} style={{ position: 'relative' }}>
                    <button
                        className="columns-btn"
                        onClick={() => setShowColumnPopover(!showColumnPopover)}
                    >
                        <FontAwesomeIcon icon={faColumns} />
                        <span>Columns</span>
                    </button>
                    {showColumnPopover && gridRef?.current && (
                        <ColumnTogglePopover
                            gridRef={gridRef}
                            onClose={() => setShowColumnPopover(false)}
                        />
                    )}
                </div>

                <button
                    className="save-button"
                    onClick={handleSave}
                    disabled={dirtyCount === 0 || isSaving}
                >
                    {isSaving ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span style={{ marginLeft: '6px' }}>Saving...</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} />
                            <span style={{ marginLeft: '6px' }}>Save</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default HeaderBar;
